/**
 * Page code is split into files loaded on demand (2026-10-12), and each release replaces those
 * files. A tab opened before a deploy then asks for a file that no longer exists; so does a page
 * loaded DURING a deploy, when the portal's two pods serve different builds for a minute or two
 * (rolling update), so a file named by one build can be asked of the other. Reload onto the new build
 * instead of showing a broken page, a few times with growing waits so a rollout in progress can finish:
 * at once, then after 3 s and 10 s. Past that (3 in 2 minutes, kept per tab) it stops, so a genuinely
 * failing load can't reload in a loop, and the error boundary offers Reload instead. A single
 * once-a-minute reload used to give up mid-rollout, and with no error boundary the page went blank
 * until a manual refresh (app.gizmodata.com, 2026-10-14/15).
 */
const FLAG = 'reloaded-for-new-build';
const WINDOW_MS = 120_000;
const DELAYS_MS = [0, 3_000, 10_000];

const recentReloads = (): number[] => {
  const raw = sessionStorage.getItem(FLAG) ?? '';
  // Older builds stored a single timestamp; a list now.
  const list = raw.startsWith('[') ? (JSON.parse(raw) as number[]) : raw ? [Number(raw)] : [];
  return list.filter((t) => Number.isFinite(t) && Date.now() - t < WINDOW_MS);
};

/** Reload onto the current build, unless this tab already tried 3 times in 2 minutes. True when a reload is under way. */
export function reloadForNewBuild(): boolean {
  let delay: number;
  try {
    const recent = recentReloads();
    if (recent.length >= DELAYS_MS.length) return false;
    delay = DELAYS_MS[recent.length];
    sessionStorage.setItem(FLAG, JSON.stringify([...recent, Date.now() + delay]));
  } catch {
    return false; // storage blocked: don't risk a loop
  }
  if (delay === 0) window.location.reload();
  else window.setTimeout(() => window.location.reload(), delay);
  return true;
}

/** Text of an error from a script/module file that failed to load. */
export const STALE_CHUNK_ERROR = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError|Loading (CSS )?chunk|Unable to preload CSS/i;

/** Vite reports a failed preload of a split file as `vite:preloadError`. */
export function installStaleBuildReload(): void {
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadForNewBuild()) event.preventDefault();
  });
}
